import { ReactElement, useLayoutEffect, useState } from 'react';
import { sortByOrder } from 'utils/collection-manipulation/array-sort-by';
import { events, markets } from 'utils/constants/sportsbook/data-fields';
import RidGenerator from 'utils/swarm/rid-generator';
import { TGameMainInfoType } from 'interfaces/sportsbook';
import { Game, Market } from 'interfaces/sportsbook-data-levels';
import { executeCommand, unsubscribeCommand } from 'services/get-data-level';
import { MarketList } from 'components/MarketsList';
import { EmptyMarketsView } from 'components/MarketsList/EmptyMarketsView';
import { MarketsListOnlySkeleton } from 'components/MarketsList/MarketsListOnlySkeleton';
import { useMounted } from 'hooks/useMounted';

type TExpandedMarketsProps = {
  game: TGameMainInfoType;
  isLive: boolean;
  handleShowMoreClick: () => void;
};

const EVENTS_SKELETON_COUNT = 3;

export const ExpandedMarkets = ({
  game,
  isLive,
  handleShowMoreClick
}: TExpandedMarketsProps): ReactElement => {
  const [marketsData, setMarketsData] = useState<Market[] | null>(null);
  const { mounted } = useMounted();

  useLayoutEffect(() => {
    if (game.id) {
      const rId = RidGenerator.gForSubscribe(`GET_GAME_MARKETS`);

      const what = {
        market: markets.market,
        event: events.event
      };

      const where = {
        game: { id: game.id }
      };

      const onResponse = (data: { market: Record<string, Market> }) => {
        if (data && data.market && mounted.current) {
          setMarketsData(sortByOrder(Object.values(data.market)));
        }
      };

      executeCommand(rId, what, where, onResponse, true, onResponse);

      return () => {
        unsubscribeCommand(rId);
      };
    }
  }, [game.id]);

  return (
    <div className="expandedMarketsWrapper">
      {marketsData ? (
        marketsData.length === 0 ? (
          <EmptyMarketsView />
        ) : (
          <MarketList
            markets={marketsData}
            game={game as Game}
            sportName={game.sportName || ''}
            competitionName={game.competitionName}
            isLive={isLive}
            currentSport={game.sport_alias}
            regionAlias={game.regionAlias}
            gameId={game.id}
            competitionId={game.competitionId}
            compactView
            handleShowMoreClick={handleShowMoreClick}
            sport={game.sport_alias}
          />
        )
      ) : (
        <MarketsListOnlySkeleton eventsSkeletonCount={EVENTS_SKELETON_COUNT} />
      )}
    </div>
  );
};
